// Exact decimal arithmetic for calculated amounts: card commission (rule C-4), and later invoice lines, VAT and
// costing. A value is an integer with a scale (n / 10^s), held as a BigInt, so nothing passes through binary
// floating point. Rounding happens only where a rule says so, with roundHalfAway (half away from zero, rule P-1),
// and every caller states the scale it rounds to.

export interface Dec { n: bigint; s: number }

const P10 = (k: number) => 10n ** BigInt(k);
const abs = (x: bigint) => (x < 0n ? -x : x);

/** Parses signed decimal text ("12.5", "-0.125", "3"). Throws on anything else: callers pass checked values. */
export function dec(x: string): Dec {
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(x.trim());
  if (!m) throw new Error(`not a decimal: ${x}`);
  const f = m[3] ?? "";
  const n = BigInt(m[2]! + f);
  return { n: m[1] ? -n : n, s: f.length };
}

const align = (a: Dec, b: Dec): [bigint, bigint, number] => {
  const s = Math.max(a.s, b.s);
  return [a.n * P10(s - a.s), b.n * P10(s - b.s), s];
};
export const add = (a: Dec, b: Dec): Dec => { const [x, y, s] = align(a, b); return { n: x + y, s }; };
export const sub = (a: Dec, b: Dec): Dec => { const [x, y, s] = align(a, b); return { n: x - y, s }; };
export const mul = (a: Dec, b: Dec): Dec => ({ n: a.n * b.n, s: a.s + b.s });
export const cmp = (a: Dec, b: Dec): number => { const [x, y] = align(a, b); return x === y ? 0 : x < y ? -1 : 1; };
export const sum = (xs: Dec[]): Dec => xs.reduce(add, { n: 0n, s: 0 });

/** Integer division of n by d, rounded half away from zero. */
function divHalfAway(n: bigint, d: bigint): bigint {
  const q = n / d, r = n % d;
  if (2n * abs(r) >= abs(d)) return q + ((n < 0n) !== (d < 0n) ? -1n : 1n);
  return q;
}

/** Rounds to `scale` decimals, half away from zero (2.345 → 2.35, -2.345 → -2.35). */
export function roundHalfAway(a: Dec, scale: number): Dec {
  if (a.s <= scale) return { n: a.n * P10(scale - a.s), s: scale };
  return { n: divHalfAway(a.n, P10(a.s - scale)), s: scale };
}

/** a ÷ b rounded half away from zero to `scale` decimals. Throws on division by zero. */
export function div(a: Dec, b: Dec, scale: number): Dec {
  if (b.n === 0n) throw new Error("division by zero");
  // a/b = (a.n / 10^a.s) / (b.n / 10^b.s); scaled by 10^scale.
  const num = a.n * P10(scale + b.s), den = b.n * P10(a.s);
  return { n: divHalfAway(num, den), s: scale };
}

/** Percentage of an amount, rounded to `scale` decimals: amount × rate ÷ 100. */
export const percentOf = (amount: Dec, rate: Dec, scale = 2): Dec => roundHalfAway({ n: amount.n * rate.n, s: amount.s + rate.s + 2 }, scale);

/** Fixed-scale text ("12.50"); the value must already have at most `scale` decimals (round first). */
export function fmt(a: Dec, scale = a.s): string {
  if (a.s > scale) {
    const r = roundHalfAway(a, scale);
    if (cmp(r, a) !== 0) throw new Error(`fmt would round ${fmt(a)} to ${scale} decimals`);
    a = r;
  }
  const n = a.n * P10(scale - a.s), neg = n < 0n, m = abs(n), base = P10(scale);
  const i = (m / base).toString(), f = scale ? "." + (m % base).toString().padStart(scale, "0") : "";
  return `${neg ? "-" : ""}${i}${f}`;
}
